import React from 'react'
import { Input, Button, Checkbox, Avatar, Tooltip } from 'antd'
import { ImportOutlined } from '@ant-design/icons'
import { ProductInfo, SearchProgress } from '../types'
import { isValueSelected } from '../utils'
import productImagePlaceholder from 'Assets/images/product_img_placeholder.png'

interface ProductListDropdownProps {
  searchQuery?: string
  onSearchChange: (value: string) => void
  values?: string[]
  options?: ProductInfo[]
  isMulti?: boolean
  onChange: (values: string[]) => void
  onImportClick: () => void
  maxWidth?: number
  isViewMode: boolean
  searchingMissingProducts: boolean
  searchProgress: SearchProgress | null
  localProductsInfo: { [key: string]: ProductInfo }
  hasMore?: boolean
  loading?: boolean
  onLoadMore?: () => void
  scrollRef?: any
  sentryRef?: any
  // View mode specific props
  selectedTempValues?: string[]
  onSelectedTempValuesChange?: (values: string[]) => void
  onEnterViewMode?: () => void
  onExitViewMode?: () => void
  onSaveViewMode?: (values: string[]) => void
  skipSearchForPerformance?: boolean
  onLoadProductDetails?: () => void
  updateProductInfo: (productValue: string, productData: ProductInfo) => void
  removeProductInfo: (productValue: string) => void
}

const ProductListDropdown: React.FC<ProductListDropdownProps> = ({
  searchQuery,
  onSearchChange,
  values,
  options,
  isMulti,
  onChange,
  onImportClick,
  maxWidth,
  isViewMode,
  searchingMissingProducts,
  searchProgress,
  localProductsInfo,
  hasMore,
  loading,
  onLoadMore,
  scrollRef,
  sentryRef,
  selectedTempValues,
  onSelectedTempValuesChange,
  onEnterViewMode,
  onExitViewMode,
  onSaveViewMode,
  skipSearchForPerformance,
  onLoadProductDetails,
  updateProductInfo,
  removeProductInfo
}) => {
  // Helper function to get merged options for view mode
  const getMergedOptionsForViewMode = (): ProductInfo[] => {
    if (!isViewMode) return options || []
    
    const selectedValues = values || []
    const existingOptions = options || []
    const existingOptionValues = new Set(existingOptions.map(opt => opt.value))
    
    // Find missing selected items
    const missingSelectedItems = selectedValues
      .filter(value => !existingOptionValues.has(value))
      .map(value => {
        const storedInfo = localProductsInfo[value]
        return storedInfo || {
          label: value, 
          value: value,
          image: productImagePlaceholder
        }
      })
    
    // Combine with existing selected options
    const selectedExistingOptions = existingOptions.filter(option => 
      selectedValues.includes(option.value)
    )
    
    return [...missingSelectedItems, ...selectedExistingOptions]
  }

  const currentOptions = isViewMode ? getMergedOptionsForViewMode() : (options || [])
  const currentSelectedValues = isViewMode ? (selectedTempValues || []) : (values || [])

  return (
    <div
      style={{
        width: Math.max(maxWidth || 0, 328),
        boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.12)',
        borderRadius: '4px',
        padding: '0',
        backgroundColor: '#fff',
      }}
    >
      {/* Header with search and import */}
      {!isViewMode && (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row',
          gap: '8px', 
          padding: '16px 16px 12px',
          borderBottom: '1px solid #f0f0f0' 
        }}>
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ flex: 1 }}
            prefix={<span className="anticon" style={{ color: '#bfbfbf' }}>🔍</span>}
          />
          {isMulti && (
            <Button
              icon={<ImportOutlined />}
              onClick={onImportClick}
              title="Import Product IDs"
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                padding: '0 8px',
                height: '32px',
                minWidth: '32px'
              }}
              size="small"
            />
          )}
        </div>
      )}

      {/* Select All section for listing mode */}
      {isMulti && !isViewMode && currentOptions.length > 0 && (
        <div
          style={{
            padding: '8px 16px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#fafafa',
          }}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              type="link"
              size="small"
              style={{ padding: '0', fontSize: '13px', fontWeight: 500 }}
              onClick={() => {
                // Select all current options
                const allCurrentValues = currentOptions.map(option => option.value)
                const newValues = [...new Set([...(values || []), ...allCurrentValues])]
                onChange(newValues)
                
                // Store product info for all selected options
                allCurrentValues.forEach((value, index) => {
                  const originalOption = currentOptions[index]
                  updateProductInfo(value, originalOption)
                })
              }}
            >
              Select All ({currentOptions.length})
            </Button>
            
            <Button
              type="link"
              size="small"
              style={{ padding: '0', fontSize: '13px', color: '#ff4d4f' }}
              onClick={() => {
                // Deselect all current options
                const currentOptionValues = currentOptions.map(option => option.value)
                const newValues = (values || []).filter(value => 
                  !currentOptionValues.some(opt => isValueSelected(opt, [value]))
                )
                onChange(newValues)
                
                // Remove product info for deselected options
                currentOptionValues.forEach(value => {
                  removeProductInfo(value)
                })
              }}
            >
              Clear All
            </Button>
          </div>
          
          <span style={{ fontSize: '13px', color: '#8c8c8c' }}>
            {(values || []).filter(value => 
              currentOptions.some(opt => isValueSelected(opt.value, [value]))
            ).length} selected
          </span>
        </div>
      )}

      {/* Selected count and actions */}
      {values?.length ? (
        <div
          style={{
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: isViewMode ? 'none' : '1px solid #f0f0f0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{ fontSize: '14px', color: '#595959', fontWeight: 500 }}
            >{`${values?.length} selected`}</div>
            {searchingMissingProducts && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '12px',
                color: '#1890ff'
              }}>
                <div 
                  style={{ 
                    width: '12px', 
                    height: '12px', 
                    border: '1px solid #f3f3f3',
                    borderTop: '1px solid #1890ff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <span>
                  {searchProgress 
                    ? `Loading products... (${searchProgress.current}/${searchProgress.total})`
                    : 'Loading products...'
                  }
                </span>
              </div>
            )}
            {skipSearchForPerformance && isViewMode && onLoadProductDetails && (
              <Button
                type="link"
                size="small"
                style={{ 
                  padding: '0', 
                  fontSize: '12px', 
                  color: '#1890ff' 
                }}
                onClick={onLoadProductDetails}
              >
                Load product details
              </Button>
            )}
          </div>
          {!isViewMode && onEnterViewMode && (
            <Button
              onClick={onEnterViewMode}
              type="link"
              style={{ 
                padding: '0', 
                fontSize: '14px', 
                fontWeight: 500, 
                color: '#1890ff' 
              }}
            >
              View
            </Button>
          )}
        </div>
      ) : null}

      {/* Product list */}
      <div 
        style={{ 
          overflowY: 'auto', 
          maxHeight: 350, 
          padding: '0 0'
        }} 
        ref={scrollRef}
      >
        {/* Select All for view mode */}
        {isMulti && isViewMode && currentOptions.length > 0 && (
          <div
            style={{
              padding: '12px 16px 4px 16px',
              borderBottom: '1px solid #f0f0f0',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Checkbox
              checked={
                currentOptions.length > 0 && 
                currentOptions.every(option => currentSelectedValues?.includes(option.value))
              }
              indeterminate={
                currentOptions.some(option => currentSelectedValues?.includes(option.value)) &&
                !currentOptions.every(option => currentSelectedValues?.includes(option.value))
              }
              onChange={(e) => {
                if (e.target.checked) {
                  // Select all items in view mode
                  const allViewModeValues = currentOptions.map(option => option.value)
                  if (onSelectedTempValuesChange) {
                    onSelectedTempValuesChange([...new Set([...allViewModeValues])])
                  }
                } else {
                  // Deselect all items in view mode
                  if (onSelectedTempValuesChange) {
                    onSelectedTempValuesChange([])
                  }
                }
              }}
            >
              <span style={{ fontWeight: 500 }}>
                Select All ({currentOptions.length} items)
              </span>
            </Checkbox>
            
            <span style={{ fontSize: '14px', color: '#8c8c8c' }}>
              {currentSelectedValues?.length || 0} selected
            </span>
          </div>
        )}

        {/* List items */}
        {currentOptions.map((option, index) => {
          const isMissingItem = isViewMode && !options?.some(opt => opt.value === option.value)
          const isSearchingThisProduct = searchingMissingProducts && isMissingItem && !localProductsInfo[option.value]
          
          // Get display label and product ID
          const displayLabel = option.label
          const productId = (option.value ?? '').toString()
          
          return (
            <div
              key={`${option.value}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor: isMissingItem ? '#fafafa' : 'transparent',
                transition: 'background-color 0.2s',
                opacity: isSearchingThisProduct ? 0.7 : 1,
              }}
              className="hover:bg-blue_1"
            >
              <Checkbox
                style={{ 
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}
                checked={isValueSelected(option.value, currentSelectedValues)}
                onChange={(e) => {
                  const isCurrentlySelected = isValueSelected(option.value, currentSelectedValues)
                  
                  if (isViewMode && onSelectedTempValuesChange) {
                    if (!isMulti) {
                      onSelectedTempValuesChange(isCurrentlySelected ? [] : [option.value])
                      return
                    }

                    const newList = isCurrentlySelected
                      ? currentSelectedValues?.filter((item) => !isValueSelected(option.value, [item])) || []
                      : [...(currentSelectedValues || []), option.value]
                    onSelectedTempValuesChange(newList)
                    return
                  } else {
                    // Listing mode
                    if (!isMulti) {
                      const newValues = isCurrentlySelected ? [] : [option.value]
                      onChange(newValues)
                      
                      // Update product info
                      if (isCurrentlySelected) {
                        removeProductInfo(option.value)
                      } else {
                        updateProductInfo(option.value, option)
                      }
                      return
                    }

                    const newList = isCurrentlySelected
                      ? (values || []).filter((item) => !isValueSelected(option.value, [item]))
                      : [...(values || []), option.value]
                    onChange(newList)
                    
                    // Update product info
                    if (isCurrentlySelected) {
                      removeProductInfo(option.value)
                    } else {
                      updateProductInfo(option.value, option)
                    }
                    return
                  }
                }}
                value={option.value}
              />
              <div
                style={{ 
                  width: 40, 
                  height: 40, 
                  marginLeft: 12, 
                  marginRight: 12, 
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}
              >
                {isSearchingThisProduct ? (
                  <div 
                    style={{ 
                      width: '16px', 
                      height: '16px', 
                      border: '2px solid #f3f3f3',
                      borderTop: '2px solid #1890ff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                ) : (
                  <Avatar shape="square" src={option?.image ?? productImagePlaceholder} size={40} />
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Tooltip title={displayLabel} placement="right">
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 500, 
                    color: '#262626',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                  }}>
                    {isSearchingThisProduct ? 'Searching...' : displayLabel}
                    {isMissingItem && !isSearchingThisProduct && !localProductsInfo[option.value] && (
                      <span style={{ color: '#bfbfbf', fontSize: '12px', fontWeight: 'normal' }}>
                        {' (Not found)'}
                      </span>
                    )}
                  </div>
                </Tooltip>
                <div style={{
                  fontSize: '12px',
                  color: '#8c8c8c',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }}>
                  {productId}
                </div>
              </div>
            </div>
          )
        })}

        {/* Load more section */}
        {(hasMore || loading) && !isViewMode && (
          <div
            ref={sentryRef}
            style={{
              textAlign: 'center',
              padding: '12px 16px',
              fontSize: '14px',
              color: '#8c8c8c',
              borderTop: '1px solid #f0f0f0',
              backgroundColor: '#fafafa',
            }}
          >
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <div 
                  style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #1890ff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <span>Loading more products...</span>
              </div>
            ) : hasMore ? (
              <div 
                style={{ 
                  cursor: 'pointer', 
                  color: '#1890ff',
                  fontWeight: 500
                }} 
                onClick={() => {
                  if (onLoadMore) {
                    onLoadMore()
                  }
                }}
              >
                Load more products
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Footer buttons for view mode */}
      {isViewMode && onExitViewMode && onSaveViewMode && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
            padding: '12px 16px',
            borderTop: '1px solid #f0f0f0',
            backgroundColor: '#fafafa',
          }}
        >
          <Button
            onClick={onExitViewMode}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => onSaveViewMode(currentSelectedValues)}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  )
}

export default ProductListDropdown 